import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MembershipPlanResponse, MembershipResponse, FamilyMemberResponse } from '../../../../../../@shared/models/response.module';
import { AddMemberModal } from '../add-member-modal/add-member-modal';
import { EditMemberModal } from '../edit-member-modal/edit-member-modal';
import { AddFamilyMemberModal } from '../add-family-member-modal/add-family-member-modal';
import { EndpointService } from '../../../../../../@shared/endpoints.service';
import { ToastService } from '../../../../../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { DeleteModal } from '../../../../../../@shared/components/delete-modal/delete-modal';

@Component({
  selector: 'app-view-plan-members',
  standalone: false,
  templateUrl: './view-plan-members.html',
  styleUrl: './view-plan-members.css'
})
export class ViewPlanMembers implements OnInit {
  @Input() plan: MembershipPlanResponse;
  @Input() memberships: MembershipResponse[] = [];
  @Input() companyId: string;

  filteredMemberships: MembershipResponse[] = [];
  searchQuery: string = '';
  statusFilter: string = 'all';
  expandedFamilies: Set<number> = new Set();

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private endpointService: EndpointService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.applyFilters();
  }

  applyFilters() {
    this.filteredMemberships = this.memberships.filter(m => {
      // Status filter
      if (this.statusFilter !== 'all' && m.status !== this.statusFilter) {
        return false;
      }

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        const userName = this.getMemberName(m).toLowerCase();
        const userEmail = this.getMemberEmail(m).toLowerCase();
        if (!userName.includes(query) && !userEmail.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }

  onSearchChange() {
    this.applyFilters();
  }

  onStatusFilterChange() {
    this.applyFilters();
  }

  openAddMemberModal() {
    const modalRef = this.modalService.open(AddMemberModal, { size: 'lg', centered: true });
    modalRef.componentInstance.plan = this.plan;
    modalRef.componentInstance.companyId = this.companyId;

    modalRef.result.then((newMembership: MembershipResponse) => {
      if (newMembership) {
        this.memberships = [newMembership, ...this.memberships];
        this.applyFilters();
      }
    }).catch(() => {});
  }

  getMemberName(membership: MembershipResponse): string {
    // Check for manual member first
    if (membership.owner_type === 'manual' && (membership.member_first_name || membership.member_last_name)) {
      return `${membership.member_first_name || ''} ${membership.member_last_name || ''}`.trim();
    }
    // Check owner_user
    if (membership.owner_user) {
      if (membership.owner_user.full_name) {
        return membership.owner_user.full_name;
      }
      if (membership.owner_user.first_name || membership.owner_user.last_name) {
        return `${membership.owner_user.first_name || ''} ${membership.owner_user.last_name || ''}`.trim();
      }
    }
    // Check family - get the owner's name instead of family name
    if (membership.family) {
      const owner = this.getFamilyOwner(membership);
      if (owner?.user) {
        if (owner.user.full_name) {
          return owner.user.full_name;
        }
        if (owner.user.first_name || owner.user.last_name) {
          return `${owner.user.first_name || ''} ${owner.user.last_name || ''}`.trim();
        }
      }
      // Fallback to family name if no owner found
      if (membership.family.name) {
        return membership.family.name;
      }
    }
    // Fallback to showing member ID
    return `Member #${membership.membership_id}`;
  }

  getMemberInitial(membership: MembershipResponse): string {
    // Check for manual member
    if (membership.owner_type === 'manual' && membership.member_first_name) {
      return membership.member_first_name.charAt(0).toUpperCase();
    }
    if (membership.owner_user) {
      if (membership.owner_user.first_name) {
        return membership.owner_user.first_name.charAt(0).toUpperCase();
      }
      if (membership.owner_user.full_name) {
        return membership.owner_user.full_name.charAt(0).toUpperCase();
      }
    }
    // Check family - get owner's initial
    if (membership.family) {
      const owner = this.getFamilyOwner(membership);
      if (owner?.user?.first_name) {
        return owner.user.first_name.charAt(0).toUpperCase();
      }
      if (owner?.user?.full_name) {
        return owner.user.full_name.charAt(0).toUpperCase();
      }
    }
    return '#';
  }

  // Family helper methods
  getFamilyOwner(membership: MembershipResponse): FamilyMemberResponse | null {
    if (!membership.family?.members) return null;
    return membership.family.members.find(m => m.role === 'owner') || membership.family.members[0] || null;
  }

  getFamilyMembers(membership: MembershipResponse): FamilyMemberResponse[] {
    return membership.family?.members || [];
  }

  getFamilyMemberCount(membership: MembershipResponse): number {
    return membership.family?.members?.length || 0;
  }

  toggleFamilyExpand(membership: MembershipResponse): void {
    if (!membership.family) return;
    const familyId = membership.family.family_id;
    if (this.expandedFamilies.has(familyId)) {
      this.expandedFamilies.delete(familyId);
    } else {
      this.expandedFamilies.add(familyId);
    }
  }

  isFamilyExpanded(membership: MembershipResponse): boolean {
    return membership.family ? this.expandedFamilies.has(membership.family.family_id) : false;
  }

  getFamilyMemberName(member: FamilyMemberResponse): string {
    if (member.user.full_name) {
      return member.user.full_name;
    }
    if (member.user.first_name || member.user.last_name) {
      return `${member.user.first_name || ''} ${member.user.last_name || ''}`.trim();
    }
    return `User #${member.user.user_id}`;
  }

  getFamilyMemberInitial(member: FamilyMemberResponse): string {
    if (member.user.first_name) {
      return member.user.first_name.charAt(0).toUpperCase();
    }
    if (member.user.full_name) {
      return member.user.full_name.charAt(0).toUpperCase();
    }
    return '#';
  }

  getRoleBadgeClass(role: string): string {
    switch (role) {
      case 'owner': return 'badge-light-primary';
      case 'guardian': return 'badge-light-success';
      case 'child': return 'badge-light-info';
      default: return 'badge-light-secondary';
    }
  }

  getMemberEmail(membership: MembershipResponse): string {
    // Check for manual member
    if (membership.owner_type === 'manual' && membership.member_email) {
      return membership.member_email;
    }
    return membership.owner_user?.email || '-';
  }

  getMemberPhone(membership: MembershipResponse): string {
    // Check for manual member
    if (membership.owner_type === 'manual' && membership.member_phone) {
      return membership.member_phone;
    }
    return membership.owner_user?.phone || '-';
  }

  getPaymentMethodLabel(method: string | null | undefined): string {
    if (!method) return '-';
    const labels: { [key: string]: string } = {
      'stripe': 'Stripe',
      'manual': 'Manual',
      'bank_transfer': 'Bank Transfer',
      'cash': 'Cash'
    };
    return labels[method] || method;
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active': return 'badge-light-success';
      case 'pending': return 'badge-light-warning';
      case 'expired': return 'badge-light-danger';
      case 'cancelled': return 'badge-light-secondary';
      default: return 'badge-light-info';
    }
  }

  getPaymentBadgeClass(status: string): string {
    switch (status) {
      case 'paid': return 'badge-light-success';
      case 'pending': return 'badge-light-warning';
      case 'refunded': return 'badge-light-info';
      case 'not_required': return 'badge-light-secondary';
      default: return 'badge-light-secondary';
    }
  }

  formatDate(date: Date | null): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  get activeCount(): number {
    return this.memberships.filter(m => m.status === 'active').length;
  }

  get pendingCount(): number {
    return this.memberships.filter(m => m.status === 'pending').length;
  }

  get expiredCount(): number {
    return this.memberships.filter(m => m.status === 'expired').length;
  }

  // Edit membership
  openEditModal(membership: MembershipResponse, event: Event) {
    event.stopPropagation();
    const modalRef = this.modalService.open(EditMemberModal, { size: 'lg', centered: true });
    modalRef.componentInstance.membership = membership;

    modalRef.result.then((updatedMembership: MembershipResponse) => {
      if (updatedMembership) {
        // Update the membership in the list
        const index = this.memberships.findIndex(m => m.membership_id === updatedMembership.membership_id);
        if (index !== -1) {
          this.memberships[index] = updatedMembership;
          this.applyFilters();
        }
      }
    }).catch(() => {});
  }

  // Delete membership
  deleteMembership(membership: MembershipResponse, event: Event) {
    event.stopPropagation();
    const modalRef = this.modalService.open(DeleteModal, { centered: true });
    modalRef.componentInstance.description = this.translateService.instant(
      'companyProfile.membershipPlans.editMember.deleteConfirm'
    );

    modalRef.result.then((confirmed) => {
      if (confirmed) {
        this.endpointService.aqh_mem_3({ membership_id: membership.membership_id, company_id: this.companyId }).subscribe({
          next: () => {
            this.toastService.success(
              this.translateService.instant('companyProfile.membershipPlans.editMember.deleteSuccess')
            );
            // Remove from list
            this.memberships = this.memberships.filter(m => m.membership_id !== membership.membership_id);
            this.applyFilters();
          },
          error: (err) => {
            console.error('Error deleting membership:', err);
            this.toastService.error(
              this.translateService.instant('companyProfile.membershipPlans.editMember.deleteError')
            );
          }
        });
      }
    }).catch(() => {});
  }

  // Family member management
  openAddFamilyMemberModal(membership: MembershipResponse, event: Event) {
    event.stopPropagation();
    if (!membership.family) return;

    const modalRef = this.modalService.open(AddFamilyMemberModal, { size: 'lg', centered: true });
    modalRef.componentInstance.familyId = membership.family.family_id;
    modalRef.componentInstance.companyId = this.companyId;

    modalRef.result.then((newMember: FamilyMemberResponse) => {
      if (newMember && membership.family) {
        membership.family.members = [...membership.family.members, newMember];
      }
    }).catch(() => {});
  }

  removeFamilyMember(membership: MembershipResponse, member: FamilyMemberResponse, event: Event) {
    event.stopPropagation();
    if (!membership.family) return;

    const modalRef = this.modalService.open(DeleteModal, { centered: true });
    modalRef.componentInstance.description = this.translateService.instant(
      'companyProfile.membershipPlans.editMember.removeFamilyMemberConfirm',
      { name: this.getFamilyMemberName(member) }
    );

    modalRef.result.then((confirmed) => {
      if (confirmed && membership.family) {
        this.endpointService.aqh_fam_0({
          family_id: membership.family.family_id,
          user_id: member.user.user_id,
          company_id: this.companyId
        }).subscribe({
          next: () => {
            this.toastService.success(
              this.translateService.instant('companyProfile.membershipPlans.editMember.familyMemberRemoved')
            );
            // Remove from local list
            if (membership.family) {
              membership.family.members = membership.family.members.filter(
                m => m.user.user_id !== member.user.user_id
              );
            }
          },
          error: (err) => {
            console.error('Error removing family member:', err);
            this.toastService.error(
              this.translateService.instant('companyProfile.membershipPlans.editMember.familyMemberRemoveError')
            );
          }
        });
      }
    }).catch(() => {});
  }

  isOwner(member: FamilyMemberResponse): boolean {
    return member.role === 'owner';
  }
}
